import { useCallback, useEffect, useState } from 'react'
import apiClient from '../api/client'
import EmptyState from '../components/EmptyState'
import ErrorAlert from '../components/ErrorAlert'
import LoadingSpinner from '../components/LoadingSpinner'

/**
 * Leads de prueba del canal del socio. **Solo TEST.**
 *
 * Reemplaza al precalificador en el ensayo. Registrar un número acá hace que,
 * cuando esa persona le escriba al WhatsApp del COE, el puente le abra la
 * sesión sola y su conversación entre por `POST /v1` — el mismo camino que va a
 * recorrer la de Software en producción. Sin registro, ese número sigue viendo
 * el menú de siempre.
 *
 * El nombre y el DNI tienen que ser los REALES del tester: §9.17 compara el
 * titular que Textract lee del documento contra el nombre de la sesión y
 * descuenta uno de los 3 intentos si no coinciden. Con datos inventados, un DNI
 * perfecto sería rechazado y la prueba mediría otra cosa.
 *
 * La pantalla no se compila en el bundle de PROD (ver `App.tsx`), y aunque
 * alguien llegara a la ruta el backend responde 404 fuera de TEST: registrar un
 * lead en producción sería fabricar una precalificación y mandarle un WhatsApp
 * a una persona que nunca habló con nosotros.
 */

interface Sesion {
  activa: boolean
  session_id?: string
  estado?: string
  ultima_actividad?: string
  nota?: string
}

interface Lead {
  telefono: string
  nombre: string
  dni: string
  capabilities: string[]
  registrado_por?: string
  registrado_at?: string
  sesion?: Sesion
}

function fmtPhone(phone: string) {
  return `+51 ${phone.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3')}`
}

function fmtFecha(ts?: string) {
  if (!ts) return '—'
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('es-PE', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  })
}

function ChipSesion({ sesion }: { sesion?: Sesion }) {
  if (!sesion || (!sesion.activa && !sesion.session_id)) {
    return (
      <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
        sin sesión · entra con menú
      </span>
    )
  }
  if (!sesion.activa) {
    return (
      <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700">
        liberada · volvió al menú
      </span>
    )
  }
  return (
    <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
      en sesión{sesion.estado ? ` · ${sesion.estado.toLowerCase()}` : ''}
    </span>
  )
}

export default function LeadsSocio() {
  const [leads, setLeads] = useState<Lead[]>([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [guardando, setGuardando] = useState(false)
  const [form, setForm] = useState({ telefono: '', nombre: '', dni: '' })

  const cargar = useCallback(async () => {
    setCargando(true)
    setError('')
    try {
      const { data } = await apiClient.get<{ leads: Lead[] }>('/test-leads')
      setLeads(data.leads ?? [])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar el registro')
    } finally {
      setCargando(false)
    }
  }, [])

  useEffect(() => { void cargar() }, [cargar])

  async function registrar(ev: React.FormEvent) {
    ev.preventDefault()
    setGuardando(true)
    setError('')
    try {
      await apiClient.post('/test-leads', form)
      setForm({ telefono: '', nombre: '', dni: '' })
      await cargar()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo registrar')
    } finally {
      setGuardando(false)
    }
  }

  async function darDeBaja(telefono: string) {
    setError('')
    try {
      await apiClient.delete(`/test-leads/${telefono}`)
      await cargar()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo dar de baja')
    }
  }

  const completo = form.telefono.trim() && form.nombre.trim() && form.dni.trim()

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900">
        <p className="font-medium">Esto reemplaza al precalificador, solo en TEST.</p>
        <p className="mt-1 text-sky-800">
          Un número registrado acá entra por la API del socio cuando escribe, igual que
          entrarán los clientes de Software en producción. Un número sin registrar sigue
          viendo el menú de siempre.
        </p>
        <p className="mt-1 text-sky-800">
          El nombre y el DNI tienen que ser los <b>reales del tester</b>: el bot compara el
          titular del documento contra el nombre de la sesión, y con datos inventados
          rechaza un DNI que está perfecto.
        </p>
      </div>

      {error && <ErrorAlert message={error} />}

      <form onSubmit={registrar} className="rounded-lg border border-slate-200 bg-white p-4">
        <h3 className="mb-3 text-sm font-semibold text-slate-800">Registrar un tester</h3>
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="text-xs font-medium text-slate-600">
            Teléfono
            <input
              value={form.telefono}
              onChange={(e) => setForm({ ...form, telefono: e.target.value })}
              placeholder="997160286"
              className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-900"
            />
          </label>
          <label className="text-xs font-medium text-slate-600">
            Nombre del lead
            <input
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              placeholder="Como figura en su DNI"
              className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-900"
            />
          </label>
          <label className="text-xs font-medium text-slate-600">
            DNI
            <input
              value={form.dni}
              onChange={(e) => setForm({ ...form, dni: e.target.value })}
              placeholder="8 dígitos"
              className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-900"
            />
          </label>
        </div>
        <button
          type="submit"
          disabled={!completo || guardando}
          className="mt-3 rounded bg-slate-900 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40"
        >
          {guardando ? 'Registrando…' : 'Registrar'}
        </button>
      </form>

      {cargando ? (
        <LoadingSpinner />
      ) : leads.length === 0 ? (
        <EmptyState
          title="Ningún tester registrado"
          description="Sin registros, todos los números entran por el camino de siempre con su menú."
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Teléfono</th>
                <th className="px-3 py-2 text-left font-medium">Nombre</th>
                <th className="px-3 py-2 text-left font-medium">DNI</th>
                <th className="px-3 py-2 text-left font-medium">Estado</th>
                <th className="px-3 py-2 text-left font-medium">Registrado</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {leads.map((l) => (
                <tr key={l.telefono}>
                  <td className="whitespace-nowrap px-3 py-2 font-medium text-slate-900">
                    {fmtPhone(l.telefono)}
                  </td>
                  <td className="px-3 py-2 text-slate-700">{l.nombre}</td>
                  <td className="px-3 py-2 tabular-nums text-slate-700">{l.dni}</td>
                  <td className="px-3 py-2"><ChipSesion sesion={l.sesion} /></td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-500">
                    {fmtFecha(l.registrado_at)}
                    {l.registrado_por ? ` · ${l.registrado_por}` : ''}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => void darDeBaja(l.telefono)}
                      className="text-xs font-medium text-slate-500 hover:text-red-600"
                    >
                      Dar de baja
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-slate-500">
        Dar de baja evita que el próximo mensaje abra una sesión nueva. Una conversación ya
        abierta sigue donde está hasta que se libere.
      </p>
    </div>
  )
}
